import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import DocumentToolbar from './DocumentToolbar';
import COMPANY_INFO from '../../config/companyInfo';
import api from '../../services/api';

function useShipmentType() {
  const { pathname } = useLocation();
  if (pathname.includes('/air-import/')) return 'air-import';
  if (pathname.includes('/ocean-import/')) return 'ocean-import';
  if (pathname.includes('/ocean-export/')) return 'ocean-export';
  if (pathname.includes('/misc/')) return 'misc';
  return 'truck';
}

function normalizeShipment(raw, type) {
  if (type === 'air-import') {
    const h = (raw.hawbs && raw.hawbs[0]) || {};
    return {
      file_no: raw.file_no, post_date: raw.post_date,
      mbl_no: raw.mawb_no || '', hbl_no: h.hawb_no || '',
      vessel_flight_no: raw.flight_no || '',
      customer: h.customer || '', consignee: h.consignee || '',
      shipper: h.shipper || '', bill_to: h.bill_to || '',
      trucker: h.trucker || '', customer_ref_no: h.customer_ref_po || '',
      carrier_bkg_no: raw.carrier || '',
      port_of_loading: raw.departure || '', port_of_discharge: raw.destination || '',
      final_destination: h.final_destination || raw.destination || '',
      package_count: raw.package_count || h.package_count || '',
      package_type: raw.pkg_type || h.pkg_type || '',
      weight_kg: raw.gross_weight_kg || h.gross_weight_kg || '',
      measurement_cbm: raw.measurement_cbm || h.measurement_cbm || '',
      memo: raw.memo || h.memo || '', created_at: raw.created_at,
    };
  }
  return raw;
}

const BillOfLading = () => {
  const { id } = useParams();
  const shipmentType = useShipmentType();
  const contentRef = useRef(null);

  const [shipment, setShipment] = useState(null);
  const [containers, setContainers] = useState([]);
  const [shipperInfo, setShipperInfo] = useState(null);
  const [consigneeInfo, setConsigneeInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchData(); }, [id, shipmentType]);

  const fetchTP = async (name) => {
    if (!name) return null;
    try {
      const res = await api.get(`/trade-partners/search?q=${encodeURIComponent(name)}`);
      if (Array.isArray(res.data) && res.data.length > 0) return res.data[0];
    } catch {}
    return null;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/${shipmentType}/${id}`);
      const s = normalizeShipment(res.data, shipmentType);
      setShipment(s);

      if (shipmentType === 'truck' || shipmentType.includes('ocean')) {
        try {
          const contRes = await api.get(`/containers/shipment/${id}`);
          setContainers(Array.isArray(contRes.data) ? contRes.data : []);
        } catch { setContainers([]); }
      }

      const [ship, cons] = await Promise.all([fetchTP(s.shipper), fetchTP(s.consignee)]);
      setShipperInfo(ship); setConsigneeInfo(cons);
      setError(null);
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  if (loading) return <div style={{ padding: '20px', textAlign: 'center' }}>Loading Bill of Lading...</div>;
  if (error) return <div style={{ padding: '20px', color: '#d32f2f' }}>Error: {error}</div>;
  if (!shipment) return <div style={{ padding: '20px' }}>No shipment data found</div>;

  const getTPAddress = (info) => {
    if (!info) return '';
    return [info.address, info.city, info.state].filter(Boolean).join(', ');
  };

  const totalPkgs = containers.reduce((sum, c) => sum + (Number(c.pkg_count) || 0), 0);
  const totalWeight = containers.reduce((sum, c) => sum + (Number(c.weight_kg) || 0), 0);
  const totalCBM = containers.reduce((sum, c) => sum + (Number(c.measurement_cbm) || 0), 0);
  const isAir = shipmentType === 'air-import';

  return (
    <div style={{ padding: '20px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <DocumentToolbar title={isAir ? 'Air Waybill' : 'Bill of Lading'} contentRef={contentRef} pdfUrl={`/pdf/shipment/${shipmentType}/${id}/bill-of-lading`} onClose={() => window.close()} fileName={`BOL-${shipment.file_no || id}.pdf`} />
      <div ref={contentRef} style={{ backgroundColor: 'white', padding: '40px', maxWidth: '900px', margin: '20px auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', fontFamily: 'Arial, sans-serif', fontSize: '12px', lineHeight: '1.4' }}>
        {/* Company Header */}
        <div style={{ textAlign: 'center', marginBottom: '20px', borderBottom: '2px solid #000', paddingBottom: '15px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '4px' }}>
            {COMPANY_INFO.logo && <img src={COMPANY_INFO.logo} alt="" style={{ height: '52px', marginRight: '12px' }} />}
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{COMPANY_INFO.name}</div>
          </div>
          <div style={{ fontSize: '11px' }}>{COMPANY_INFO.address}, {COMPANY_INFO.cityStateZip}</div>
          <div style={{ fontSize: '11px' }}>Tel: {COMPANY_INFO.tel} | Email: {COMPANY_INFO.email}</div>
        </div>

        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '10px' }}>{isAir ? 'AIR WAYBILL' : 'BILL OF LADING'}</div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ fontWeight: 'bold' }}>{isAir ? 'AWB NO.:' : 'B/L NO.:'}&nbsp;</div>
            <div style={{ borderBottom: '1px solid #000', minWidth: '200px' }}>{shipment.mbl_no || shipment.hbl_no || `BOL-${id}`}</div>
          </div>
        </div>

        {/* Two Column Layout */}
        <div style={{ display: 'flex', gap: '40px', marginBottom: '25px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: 'bold', borderBottom: '1px solid #000', marginBottom: '8px', paddingBottom: '4px' }}>SHIPPER / EXPORTER</div>
              <div style={{ minHeight: '70px', fontSize: '11px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{shipment.shipper || 'N/A'}</div>
                <div>{getTPAddress(shipperInfo)}</div>
              </div>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ fontWeight: 'bold', borderBottom: '1px solid #000', marginBottom: '8px', paddingBottom: '4px' }}>CONSIGNEE</div>
              <div style={{ minHeight: '70px', fontSize: '11px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{shipment.consignee || 'N/A'}</div>
                <div>{getTPAddress(consigneeInfo)}</div>
              </div>
            </div>
            <div>
              <div style={{ fontWeight: 'bold', borderBottom: '1px solid #000', marginBottom: '8px', paddingBottom: '4px' }}>NOTIFY PARTY</div>
              <div style={{ minHeight: '70px', fontSize: '11px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{shipment.customer || 'N/A'}</div>
              </div>
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '11px' }}>FILE NO.</div>
              <div style={{ borderBottom: '1px solid #000', paddingTop: '4px', minHeight: '20px' }}>{shipment.file_no || ''}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '11px' }}>CUSTOMER REF NO.</div>
              <div style={{ borderBottom: '1px solid #000', paddingTop: '4px', minHeight: '20px' }}>{shipment.customer_ref_no || ''}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontWeight: 'bold', fontSize: '11px' }}>{isAir ? 'CARRIER' : 'CARRIER / BOOKING NO.'}</div>
              <div style={{ borderBottom: '1px solid #000', paddingTop: '4px', minHeight: '20px' }}>{shipment.carrier_bkg_no || ''}</div>
            </div>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '11px' }}>TRUCKER</div>
              <div style={{ borderBottom: '1px solid #000', paddingTop: '4px', minHeight: '20px' }}>{shipment.trucker || ''}</div>
            </div>
          </div>
        </div>

        {/* Shipping Details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '25px', borderTop: '1px solid #000', borderBottom: '1px solid #000', paddingTop: '12px', paddingBottom: '12px' }}>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>{isAir ? 'FLIGHT NO.' : 'VESSEL / VOYAGE NO.'}</div>
            <div style={{ fontSize: '12px' }}>{shipment.vessel_flight_no || ''}</div>
          </div>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>{isAir ? 'DEP. AIRPORT' : 'PORT OF LOADING'}</div>
            <div style={{ fontSize: '12px' }}>{shipment.port_of_loading || ''}</div>
          </div>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>{isAir ? 'DEST. AIRPORT' : 'PORT OF DISCHARGE'}</div>
            <div style={{ fontSize: '12px' }}>{shipment.port_of_discharge || ''}</div>
          </div>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>FINAL DESTINATION</div>
            <div style={{ fontSize: '12px' }}>{shipment.final_destination || ''}</div>
          </div>
        </div>

        {/* Cargo Table */}
        <div style={{ marginBottom: '25px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #000' }}>
                {['CONTAINER NO.', 'SIZE/TYPE', 'SEAL NO.', 'PKG COUNT', 'PKG TYPE', 'WEIGHT (KG)', 'CBM'].map(h => (
                  <th key={h} style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid #000' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {containers.length > 0 ? containers.map((c, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #ccc' }}>
                  <td style={{ padding: '8px', borderRight: '1px solid #000' }}>{c.container_no || ''}</td>
                  <td style={{ padding: '8px', borderRight: '1px solid #000' }}>{c.tp_sz || ''}</td>
                  <td style={{ padding: '8px', borderRight: '1px solid #000' }}>{c.seal_no || ''}</td>
                  <td style={{ padding: '8px', textAlign: 'center', borderRight: '1px solid #000' }}>{c.pkg_count || ''}</td>
                  <td style={{ padding: '8px', borderRight: '1px solid #000' }}>{c.pkg_type || ''}</td>
                  <td style={{ padding: '8px', textAlign: 'center', borderRight: '1px solid #000' }}>{c.weight_kg || ''}</td>
                  <td style={{ padding: '8px', textAlign: 'center' }}>{c.measurement_cbm || ''}</td>
                </tr>
              )) : (
                <tr style={{ borderBottom: '1px solid #ccc' }}>
                  <td colSpan="7" style={{ padding: '8px', textAlign: 'center', color: '#999' }}>
                    {shipment.package_count ? `${shipment.package_count} ${shipment.package_type || 'PKG(S)'} — ${shipment.weight_kg || ''} KG` : 'No containers found'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '40px', marginBottom: '25px', paddingRight: '20px', borderBottom: '2px solid #000', paddingBottom: '12px' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>TOTAL PKGS</div>
            <div>{totalPkgs || shipment.package_count || ''}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>TOTAL WEIGHT (KG)</div>
            <div>{totalWeight || shipment.weight_kg || ''}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>TOTAL CBM</div>
            <div>{totalCBM || shipment.measurement_cbm || ''}</div>
          </div>
        </div>

        {/* Signature */}
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '40px', paddingTop: '20px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>PLACE AND DATE OF ISSUE</div>
            <div style={{ borderTop: '1px solid #000', paddingTop: '4px', minHeight: '40px' }}></div>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 'bold', fontSize: '11px', marginBottom: '4px' }}>SIGNED BY (Carrier / Agent)</div>
            <div style={{ borderTop: '1px solid #000', paddingTop: '4px', minHeight: '40px' }}></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillOfLading;
